import { z } from "zod";

export const TransformSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("trim") }),
  z.object({ op: z.literal("lower") }),
  z.object({ op: z.literal("upper") }),
  z.object({ op: z.literal("title") }),
  z.object({ op: z.literal("split"), separator: z.string().min(1), index: z.number().int().optional() }),
  z.object({ op: z.literal("concat"), separator: z.string().default(" ") }),
  z.object({
    op: z.literal("valueMap"),
    map: z.record(z.string(), z.string()),
    fallback: z.enum(["keep", "empty"]).default("keep"),
  }),
  z.object({ op: z.literal("date"), input: z.enum(["auto", "us", "eu", "unix", "unix_ms"]).default("auto") }),
  z.object({ op: z.literal("phoneE164"), defaultCountry: z.string().length(2).default("US") }),
  z.object({ op: z.literal("number") }),
  z.object({ op: z.literal("boolean") }),
  z.object({ op: z.literal("default"), value: z.string() }),
  z.object({ op: z.literal("regexReplace"), pattern: z.string(), replacement: z.string().default("") }),
  z.object({ op: z.literal("list"), separator: z.string().default(",") }),
]);
export type Transform = z.infer<typeof TransformSchema>;

export type Value = string | number | boolean | string[] | null;

const CALLING_CODES: Record<string, string> = {
  US: "1", CA: "1", GB: "44", AU: "61", NZ: "64", IE: "353", DE: "49", FR: "33", ES: "34", IT: "39",
  NL: "31", BE: "32", SE: "46", NO: "47", DK: "45", FI: "358", CH: "41", AT: "43", PT: "351", PL: "48",
  SG: "65", HK: "852", JP: "81", IN: "91", ZA: "27", AE: "971", MX: "52", BR: "55",
};

export function toE164(raw: string, defaultCountry = "US"): string | null {
  const s = raw.trim();
  if (!s) return null;
  const hasPlus = s.startsWith("+") || s.startsWith("00");
  let digits = s.replace(/\D/g, "");
  if (s.startsWith("00")) digits = digits.slice(2);
  if (hasPlus) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  const cc = CALLING_CODES[defaultCountry.toUpperCase()];
  if (!cc) return null;
  if (cc === "1") {
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    return null;
  }
  if (digits.startsWith(cc) && digits.length > cc.length + 6) return `+${digits}`;
  if (digits.startsWith("0")) digits = digits.slice(1); // national trunk prefix
  const full = cc + digits;
  return full.length >= 8 && full.length <= 15 ? `+${full}` : null;
}

/** Parse many common date shapes into an ISO-8601 UTC string. */
export function parseDate(raw: string, input: "auto" | "us" | "eu" | "unix" | "unix_ms" = "auto"): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (input === "unix" || (input === "auto" && /^\d{10}$/.test(s))) {
    const d = new Date(Number(s) * 1000);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (input === "unix_ms" || (input === "auto" && /^\d{13}$/.test(s))) {
    const d = new Date(Number(s));
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  // DD/MM/YYYY or MM/DD/YYYY with optional time
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/.exec(s);
  if (m) {
    const a = Number(m[1]);
    const b = Number(m[2]);
    let day: number, month: number;
    if (input === "eu") [day, month] = [a, b];
    else if (input === "us") [month, day] = [a, b];
    else if (a > 12) [day, month] = [a, b];
    else [month, day] = [a, b]; // ambiguous → US (most export tools default to US format)
    let hour = m[4] ? Number(m[4]) : 0;
    const ampm = m[7]?.toLowerCase();
    if (ampm === "pm" && hour < 12) hour += 12;
    if (ampm === "am" && hour === 12) hour = 0;
    const d = new Date(Date.UTC(Number(m[3]), month - 1, day, hour, m[5] ? Number(m[5]) : 0, m[6] ? Number(m[6]) : 0));
    if (isNaN(d.getTime()) || d.getUTCDate() !== day) return null;
    return d.toISOString();
  }
  // "YYYY-MM-DD HH:mm:ss" (no zone) → treat as UTC
  const isoish = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s) ? s.replace(" ", "T") + "Z" : s;
  const d = new Date(isoish);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function asString(v: Value): string {
  if (v === null) return "";
  if (Array.isArray(v)) return v.join(",");
  return String(v);
}

export function applyTransform(value: Value, t: Transform): Value {
  switch (t.op) {
    case "trim":
      return Array.isArray(value) ? value.map((x) => x.trim()) : value === null ? null : asString(value).trim();
    case "lower":
      return value === null ? null : asString(value).toLowerCase();
    case "upper":
      return value === null ? null : asString(value).toUpperCase();
    case "title":
      return value === null
        ? null
        : asString(value).toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase());
    case "split": {
      const parts = asString(value).split(t.separator).map((p) => p.trim());
      return t.index === undefined ? parts : (parts[t.index < 0 ? parts.length + t.index : t.index] ?? null);
    }
    case "concat":
      return Array.isArray(value) ? value.filter((x) => x !== "").join(t.separator) : value;
    case "valueMap": {
      const key = asString(value);
      if (key in t.map) return t.map[key] ?? null;
      const lowerHit = Object.entries(t.map).find(([k]) => k.toLowerCase() === key.toLowerCase());
      if (lowerHit) return lowerHit[1];
      return t.fallback === "empty" ? null : value;
    }
    case "date":
      return value === null || asString(value) === "" ? null : parseDate(asString(value), t.input);
    case "phoneE164":
      return value === null ? null : toE164(asString(value), t.defaultCountry);
    case "number": {
      const s = asString(value).replace(/[^0-9.,-]/g, "");
      if (!s) return null;
      // "1.234,56" (EU) vs "1,234.56" (US)
      const normalised = /,\d{1,2}$/.test(s) ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
      const n = Number(normalised);
      return Number.isFinite(n) ? n : null;
    }
    case "boolean": {
      const s = asString(value).trim().toLowerCase();
      if (["true", "yes", "y", "1", "t"].includes(s)) return true;
      if (["false", "no", "n", "0", "f", ""].includes(s)) return false;
      return null;
    }
    case "default":
      return value === null || asString(value).trim() === "" ? t.value : value;
    case "regexReplace":
      return value === null ? null : asString(value).replace(new RegExp(t.pattern, "g"), t.replacement);
    case "list":
      return Array.isArray(value)
        ? value.flatMap((x) => x.split(t.separator)).map((x) => x.trim()).filter(Boolean)
        : asString(value)
            .split(t.separator)
            .map((x) => x.trim())
            .filter(Boolean);
  }
}
